/* eslint-disable indent */
const HEIGHT_SYNC_MIN_WIDTH = 1024;
const RESIZE_DEBOUNCE_DELAY = 150;

/**
 * Delay calling `fn` until `delay` ms have passed since the last call.
 * Used to keep expensive resize-driven layout recalculations from
 * running on every single 'resize' event fired mid-drag.
 *
 * @param {Function} fn
 * @param {number} delay
 * @returns {Function}
 */
function debounce(fn, delay) {
	let timeoutId;

	return (...args) => {
		clearTimeout(timeoutId);
		timeoutId = setTimeout(() => fn(...args), delay);
	};
}

class SideTabs {
	constructor(el) {
		this.el = el;
		this.tabsRow = this.el.querySelector('.block-side-tabs__tabs-row');
		this.tabsEl = this.el.querySelector('.block-side-tabs__tabs');
		this.tabs = this.el.querySelectorAll('.block-side-tabs__tab');
		this.tabContentContainer = this.el.querySelectorAll('.block-side-tabs__tab-content-container');
		this.tabContent = this.el.querySelectorAll('.block-side-tabs__tab-content');
		//this.tabContentTitle = this.el.querySelectorAll('.block-side-tabs__tab-content-title-mobile');
		this.currentMode = null;
		this.scrollOffset = 200;
		this.tabsMarginTop = 0;
		this.isChangingTab = false;
		this.syncStickyOffsets();
		this.init();

		window.requestAnimationFrame(() => {
			this.updateActiveTabMinHeight();
		});

		this.debouncedOnResize = debounce(
			this.onResize,
			RESIZE_DEBOUNCE_DELAY
		);

		window.addEventListener('resize', this.debouncedOnResize);
	}

	init = () => {
		const isDesktop = window.innerWidth > HEIGHT_SYNC_MIN_WIDTH;
		const newMode = isDesktop ? 'desktop' : 'mobile';

		if (this.currentMode === newMode) {
			return;
		}

		this.removeAllListeners();

		if (isDesktop) {
			this.initDesktop();
		} else {
			this.initMobile();
		}

		this.currentMode = newMode;
	};

	onResize = () => {
		this.syncStickyOffsets();
		this.init();

		window.requestAnimationFrame(() => {
			this.updateActiveTabMinHeight();
		});
	};

	removeAllListeners = () => {
		this.tabs.forEach((tab) => {
			tab.onclick = null;
			tab.onkeydown = null;
		});

		this.tabContentContainer.forEach((tabContent) => {
			const title = tabContent.querySelector(
				'.block-side-tabs__tabs-container-mobile'
			);

			if (title) {
				title.onclick = null;
			}
		});

		this.tabs = this.el.querySelectorAll(
			'.block-side-tabs__tab'
		);

		this.tabContentContainer = this.el.querySelectorAll(
			'.block-side-tabs__tab-content-container'
		);
	};

	getTabsOuterHeight = () => {
		if (!this.tabsEl) {
			return 0;
		}

		const tabsStyles = window.getComputedStyle(
			this.tabsEl
		);

		const marginTop =
			parseFloat(tabsStyles.marginTop) || 0;

		const marginBottom =
			parseFloat(tabsStyles.marginBottom) || 0;

		return (
			this.tabsEl.offsetHeight +
			marginTop +
			marginBottom
		);
	};

	updateActiveTabMinHeight = () => {
		this.tabContentContainer.forEach((content) => {
			content.style.removeProperty('min-height');
		});

		if (
			window.innerWidth <= HEIGHT_SYNC_MIN_WIDTH ||
			!this.tabsEl
		) {
			return;
		}

		const activeContent = [
			...this.tabContentContainer
		].find((content) =>
			content.classList.contains('active')
		);

		if (!activeContent) {
			return;
		}

		const tabsOuterHeight = this.getTabsOuterHeight();

		const contentMinHeight =
			parseFloat(
				window.getComputedStyle(activeContent).minHeight
			) || 0;

		if (tabsOuterHeight > contentMinHeight) {
			activeContent.style.minHeight =
				`${Math.ceil(tabsOuterHeight)}px`;
		}
	};

	waitForScrollToFinish = (targetTop) => {
		return new Promise((resolve) => {
			const startTime = performance.now();
			let stableFrames = 0;

			const checkPosition = () => {
				const distance = Math.abs(
					window.scrollY - targetTop
				);

				if (distance <= 2) {
					stableFrames++;
				} else {
					stableFrames = 0;
				}

				if (
					stableFrames >= 3 ||
					performance.now() - startTime >= 1200
				) {
					resolve();
					return;
				}

				window.requestAnimationFrame(checkPosition);
			};

			window.requestAnimationFrame(checkPosition);
		});
	};

	scrollToTabsStart = async () => {
		if (!this.tabsRow) {
			return;
		}

		const tabsRowTop =
			window.scrollY +
			this.tabsRow.getBoundingClientRect().top;

		const targetTop = Math.max(
			tabsRowTop - this.scrollOffset,
			0
		);

		if (window.scrollY <= targetTop + 2) {
			return;
		}

		const prefersReducedMotion = window.matchMedia(
			'(prefers-reduced-motion: reduce)'
		).matches;

		window.scrollTo({
			top: targetTop,
			behavior: prefersReducedMotion ? 'auto' : 'smooth'
		});

		if (!prefersReducedMotion) {
			await this.waitForScrollToFinish(targetTop);
		}
	};

	setActiveTab = (index) => {
		this.tabContentContainer.forEach((content) => {
			content.classList.toggle(
				'active',
				content.dataset.index === index
			);
		});

		this.syncTabIndicators(index);

		window.requestAnimationFrame(() => {
			this.updateActiveTabMinHeight();
		});
	};

	// Roving tabindex + aria-selected: only the active tab is selected
	// and reachable via Tab, matching the ARIA tabs pattern. Shared by
	// setActiveTab() (click/keyboard) and initDesktop() (re-entering
	// desktop mode after a resize), since both can change which tab
	// is active without going through the other.
	syncTabIndicators = (activeIndex) => {
		this.tabs.forEach((tab) => {
			const isActive = tab.dataset.index === activeIndex;

			tab.classList.toggle('active', isActive);

			tab.setAttribute(
				'aria-selected',
				isActive ? 'true' : 'false'
			);

			tab.setAttribute(
				'tabindex',
				isActive ? '0' : '-1'
			);
		});
	};

	handleTabKeydown = (event, currentTab) => {
		const navigationKeys = ['ArrowUp', 'ArrowDown', 'Home', 'End'];

		if (!navigationKeys.includes(event.key)) {
			return;
		}

		// Prevent the page from scrolling on Arrow/Home/End.
		event.preventDefault();

		const tabsArray = [...this.tabs];
		const currentIndex = tabsArray.indexOf(currentTab);
		const lastIndex = tabsArray.length - 1;
		let newIndex = currentIndex;

		if (event.key === 'ArrowDown') {
			newIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
		} else if (event.key === 'ArrowUp') {
			newIndex = currentIndex === 0 ? lastIndex : currentIndex - 1;
		} else if (event.key === 'Home') {
			newIndex = 0;
		} else if (event.key === 'End') {
			newIndex = lastIndex;
		}

		const newTab = tabsArray[newIndex];

		if (!newTab) {
			return;
		}

		newTab.focus();
		this.changeDesktopTab(newTab.dataset.index);
	};

	changeDesktopTab = async (index) => {
		if (this.isChangingTab) {
			return;
		}

		const activeTab = this.el.querySelector(
			'.block-side-tabs__tab.active'
		);

		this.isChangingTab = true;

		try {
			await this.scrollToTabsStart();

			if (activeTab?.dataset.index === index) {
				return;
			}

			this.setActiveTab(index);
		} finally {
			this.isChangingTab = false;
		}
	};

	initDesktop = () => {
		const activeContent = [
			...this.tabContentContainer
		].find((content) =>
			content.classList.contains('active')
		);

		if (
			!activeContent &&
			this.tabContentContainer.length > 0 &&
			this.tabs.length > 0
		) {
			this.tabContentContainer[0].classList.add(
				'active'
			);

			this.syncTabIndicators(this.tabs[0].dataset.index);
		} else if (activeContent) {
			this.syncTabIndicators(activeContent.dataset.index);
		}

		this.tabs.forEach((tab) => {
			tab.onclick = () => {
				this.changeDesktopTab(tab.dataset.index);
			};

			tab.onkeydown = (event) => {
				this.handleTabKeydown(event, tab);
			};
		});
	};

	initMobile = () => {
		this.tabContentContainer.forEach((tabContent) => {
			const title = tabContent.querySelector(
				'.block-side-tabs__tabs-container-mobile'
			);

			if (!title) {
				return;
			}

			title.onclick = () => {
				const isActive =
					tabContent.classList.contains('active');

				this.tabContentContainer.forEach((content) => {
					content.classList.remove('active');

					content
						.querySelector('.block-side-tabs__tabs-container-mobile')
						?.setAttribute('aria-expanded', 'false');
				});

				if (!isActive) {
					tabContent.classList.add('active');
					title.setAttribute('aria-expanded', 'true');
				}
			};
		});
	};

	syncStickyOffsets = () => {
		if (!this.tabsEl) {
			return;
		}

		const tabsStyles =
			window.getComputedStyle(this.tabsEl);

		const tabsMarginTop =
			parseFloat(tabsStyles.marginTop) || 0;

		this.tabsMarginTop = tabsMarginTop;

		// The media and the tabs menu must lock into place at the same
		// screen position, so both use the same combined offset: the
		// fixed scroll trigger plus the menu's own margin-top.
		const combinedOffset =
			this.scrollOffset + tabsMarginTop;

		this.el.style.setProperty(
			'--side-tabs-sticky-offset',
			`${combinedOffset}px`
		);

		this.el.style.setProperty(
			'--side-tabs-menu-sticky-offset',
			`${combinedOffset}px`
		);
	};
}

export default SideTabs;
